import { Anchor, Button, Checkbox, DesktopModal, Divider, Flex, Modal, Text, tinyButtons, Toast } from '@byecode/ui'
import { TitleInContentModal } from '@lighthouse/shared'
import cls from 'classnames'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'

import * as srv from '@/services'
import type { WxMiniProgramsAuditInfo } from '@/services/types'
import { useWxMiniProgramsAuthorizationResult } from '@/shared/reusable'

import AuditReady from './AuditReady'
import MiniProgramsInfo from './MiniProgramsInfo'

interface WeChatAuditFormModalProps {
    open: boolean
    data?: WxMiniProgramsAuditInfo
    onClose: (isSubmit?: boolean) => void
    onSubmit: () => void
}

const SCxContainer = styled.form`
    width: 100%;
    height: 100%;
    overflow: hidden;
    flex: 1;
    display: flex;
    font-size: var(---font-size-normal);
`

const SCxLeftPanel = styled.div`
    position: relative;
    width: 186px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background-color: var(--color-gray-50);
    border-right: 1px solid var(--color-gray-200);
`

const SCxRightPanel = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
`

const SCxRoundNum = styled.div`
    width: 15px;
    height: 15px;
    line-height: 15px;
    font-size: 10px;
    text-align: center;
    color: var(--color-white);
    border-radius: 100%;
    background-color: var(--color-gray-400);
`

const SCxTab = styled.div`
    display: flex;
    align-items: center;
    gap: 6.5px;
    color: var(--color-gray-400);
    font-size: var(--font-size-normal);
    &.active {
        color: var(--color-theme-7);
        font-weight: 600;
        ${SCxRoundNum} {
            background-color: var(--color-theme-7);
        }
    }
`

const SCxContent = styled.div`
    height: 100%;
    overflow-y: auto;
    flex: 1;
    ${tinyButtons()};
`

const SCxFooter = styled.div`
    width: 100%;
    padding: 12px 20px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid var(--color-gray-200);
    background-color: var(--color-white);

    > button {
        padding: 0 32px;
        height: 40px;
        line-height: 40px;
    }
`

const defaultValue: WxMiniProgramsAuditInfo = {
    hasUploadAvatar: false,
    hasFilledName: false,
    hasSelectedCategory: false,
    isFunctionalComplete: false,
    hasReadSpecification: false,
    // hasUploadAvatar: true,
    // hasFilledName: true,
    // hasSelectedCategory: true,
    // isFunctionalComplete: true,
    // hasReadSpecification: true,
    description: '', // 版本更新说明
    screenshots: [], // 截图URL（至少3张）
    screenRecords: [], // 录屏URL（可选）
    testNotes: '', // 测试备注
    version: '' // 版本号
}

const WeChatAuditFormModal: React.FunctionComponent<WeChatAuditFormModalProps> = ({ data, open, onClose, onSubmit }) => {
    const [stepIndex, setStepIndex] = useState(0)
    const methods = useForm({
        mode: 'onSubmit',
        defaultValues: data ?? defaultValue
    })
    const { watch, handleSubmit, formState, reset, getValues } = methods

    const { update } = useWxMiniProgramsAuthorizationResult()

    const [hasUploadAvatar, hasFilledName, hasSelectedCategory, isFunctionalComplete, hasReadSpecification, description] = watch([
        'hasUploadAvatar',
        'hasFilledName',
        'hasSelectedCategory',
        'isFunctionalComplete',
        'hasReadSpecification',
        'description'
    ])

    const hasNext = hasUploadAvatar && hasFilledName && hasSelectedCategory && isFunctionalComplete && hasReadSpecification

    const disabledSubmit = !description || !hasNext

    const handleMiniProgramAudi = useCallback(
        async (params: WxMiniProgramsAuditInfo) => {
            const isConfirm = await Modal.confirm({
                title: '确认提交审核？',
                okStatus: 'primary',
                content:
                    '微信审核时间约1-7自然日，微信审核通过后即完成正式版的发布，每个自然日可提交1次审核。（撤销后当天不能再次提交审核）',
                cancelText: '再想想',
                okText: '确认',
                attribute: {
                    'data-modal-ignore-click-away': true
                }
            })
            const isSave = await srv.saveWxMiniProgramsAuditInfo(params)
            if (isSave && isConfirm) {
                onClose?.(true)
                const res = await srv.submitAudit()
                if (res) {
                    onSubmit()
                    Toast.success('已提交微信审核，审核通过后即发布正式版')
                    update()
                }
            }
        },
        [onClose, update, onSubmit]
    )

    const handleClose = useCallback(() => {
        const params = getValues()
        srv.saveWxMiniProgramsAuditInfo(params)
        onClose()
    }, [getValues, onClose])

    const handleFormSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleMiniProgramAudi)()
        },
        [handleSubmit, handleMiniProgramAudi]
    )

    return (
        <DesktopModal
            styles={{
                modal: {
                    maxHeight: '80%'
                },
                body: {
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    padding: 0
                }
            }}
            title="提交微信审核"
            open={open}
            onClose={onClose}
            closeOnClickOverlay={false}
            data-modal-ignore-click-away
        >
            <SCxContainer onSubmit={handleFormSubmit}>
                <SCxLeftPanel>
                    <SCxTab className={cls({ active: stepIndex >= 0 })}>
                        <SCxRoundNum>1</SCxRoundNum>
                        <Text>提审前准备</Text>
                    </SCxTab>
                    <div style={{ width: '100%', height: 39 }}>
                        <Divider
                            style={{ position: 'absolute', left: 29, width: 1, height: 39 }}
                            color={stepIndex === 1 ? 'var(--color-theme-7)' : 'var(--color-gray-200)'}
                            direction="vertical"
                        />
                    </div>
                    <SCxTab className={cls({ active: stepIndex === 1 })}>
                        <SCxRoundNum>2</SCxRoundNum>
                        <Text>小程序信息</Text>
                    </SCxTab>
                </SCxLeftPanel>
                <SCxRightPanel>
                    <SCxContent>
                        <FormProvider {...methods}>
                            {stepIndex === 0 && <AuditReady />}
                            {stepIndex === 1 && <MiniProgramsInfo />}
                        </FormProvider>
                    </SCxContent>
                    <SCxFooter>
                        <Button
                            type="tag"
                            style={{ backgroundColor: 'var(--color-gray-100)', color: 'var(--color-gray-500)' }}
                            onClick={handleClose}
                        >
                            取消
                        </Button>
                        {stepIndex === 0 && (
                            <Button type="primary" disabled={!hasNext} onClick={() => setStepIndex(1)}>
                                下一步
                            </Button>
                        )}
                        {stepIndex === 1 && (
                            <Button
                                type="tag"
                                style={{ backgroundColor: 'var(--color-gray-100)', color: 'var(--color-gray-500)' }}
                                onClick={() => setStepIndex(0)}
                            >
                                上一步
                            </Button>
                        )}
                        {stepIndex === 1 && (
                            <Button type="primary" htmlType="submit" disabled={disabledSubmit}>
                                提交审核
                            </Button>
                        )}
                    </SCxFooter>
                </SCxRightPanel>
            </SCxContainer>
        </DesktopModal>
    )
}

export default WeChatAuditFormModal
